import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router'
import LayoutAdmin from '@/app/layouts/layout-admin'
import Dashboard from '@/app/pages/dashboard'
import NotFound from '@/app/pages/not-found'
import Unauthorized from '@/app/pages/unauthorized'
import { WhatsNewProvider } from '@/app/providers/whats-new-provider'
import { retryImport } from '@/app/router/retry-import'
import {
  loadBusinessRouter,
  loadClientsRouter,
  loadDeliveriesRouter,
  loadInventoryRouter,
  loadNegotiationsRouter
} from '@/app/router/route-preloaders'
import { NavigateAuthBusinessAdmin } from '@/app/router/router-admin'
import { SEGMENTS } from '@/app/router/routes'
import { apiTokenRoutes } from '@/features/account/api-tokens/api-tokens.routes'
import AxiosInterceptor from '@/features/auth/components/axios-interceptor'
import PersistLogin from '@/features/auth/components/persist-login'
import RequireAuth from '@/features/auth/components/require-auth'
import { AuthRouter } from '@/features/auth/router/auth-router'
import { revalidationRoutes } from '@/features/client-portal/revalidation.routes'
import { creditsRoutes } from '@/features/credits/credits.routes'
import { flowsRoutes } from '@/features/flows/flows.routes'
import { Permission } from '@/features/roles/consts/permissions'
import { TypeAccount } from '@/features/users/models/user'
import { AdminRouteLoadingSkeleton } from '@/shared/components/app-loading-skeleton'
import { DocumentHead } from '@/shared/components/document-head'
import LoadingBrand from '@/shared/components/loading-brand'
import { ProtectedRoute } from '@/shared/components/protected-route'
import { RouteErrorBoundary } from '@/shared/components/route-error-boundary'
import { FEATURES } from '@/shared/config/features'

/*
 * Los mismos loaders sirven para dos cosas distintas, y sólo UNA lleva `retryImport`.
 *
 * Acá, dentro de `lazy`, la carga es la navegación real: si el chunk no está, el usuario se
 * queda sin pantalla y hay que recuperarlo recargando. En `preloadAdminRoute` la misma función
 * corre por PASAR EL MOUSE por encima —es especulativa, el usuario ni pidió ir— y ahí una
 * recarga sería una agresión: se falla en silencio y, si después hace clic, este camino la
 * atiende como corresponde.
 */
const RouterClients = lazy(() => retryImport(loadClientsRouter))
const RouterBusiness = lazy(() => retryImport(loadBusinessRouter))
const RouterNegotiations = lazy(() => retryImport(loadNegotiationsRouter))
const RouterDeliveries = lazy(() => retryImport(loadDeliveriesRouter))
const RouterInventory = lazy(() => retryImport(loadInventoryRouter))
const ChangelogPage = lazy(() => retryImport(() => import('@/app/pages/changelog.page')))
const RouterAccounts = lazy(() =>
  retryImport(() => import('@/features/account/router/router-account'))
)
const AuthorizeReservationPage = lazy(() =>
  retryImport(() => import('@/features/inventory/pages/authorize-reservation.page'))
)
const DocumentShare = lazy(() =>
  retryImport(() => import('@/features/client-portal/pages/document-share'))
)

const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingBrand className="h-screen w-screen" />}>{children}</Suspense>
)

const AdminSuspenseWrapper = ({
  children,
  fallback = <AdminRouteLoadingSkeleton />
}: {
  children: React.ReactNode
  fallback?: React.ReactNode
}) => <Suspense fallback={fallback}>{children}</Suspense>

const routes = [
  {
    path: '/',
    element: <Navigate to={SEGMENTS.admin.root} />
  },
  {
    path: `${SEGMENTS.auth.root}/*`,
    element: <AuthRouter />
  },
  {
    path: 'authorize/:token',
    element: (
      <SuspenseWrapper>
        <AuthorizeReservationPage />
      </SuspenseWrapper>
    )
  },
  // ⚠️ Aprobación de documentos: NO va detrás de `clientPortal`, aunque su página viva en la
  // carpeta `client-portal/`. Es una función DISTINTA —un enlace firmado donde alguien aprueba o
  // rechaza un documento— que ya corre en producción sobre `document-approval-flows` (v1).
  // Gatearla dejaría en 404 a todo el que reciba un enlace de aprobación.
  //
  // Vivir en la misma carpeta no es lo mismo que ser la misma feature: el flag agrupa por
  // FUNCIÓN, no por directorio.
  {
    path: `${SEGMENTS.clientPortal.root}/${SEGMENTS.clientPortal.approvals}/:accessToken`,
    element: (
      <SuspenseWrapper>
        <DocumentShare />
      </SuspenseWrapper>
    )
  },
  // El formulario público SÍ es el portal del cliente. Con el flag apagado la ruta no se registra,
  // así que un enlace viejo cae en el 404 normal en vez de abrir un formulario no liberado.
  // Ver `docs/architecture/api-v2/inventario-superficie-visible.md`.
  ...(FEATURES.clientPortal
    ? [
        {
          path: `${SEGMENTS.clientPortal.root}/${SEGMENTS.clientPortal.forms}/:accessToken`,
          lazy: {
            Component: async () =>
              await retryImport(() => import('@/features/client-portal/pages/portal-form')).then(
                (m) => m.default
              )
          }
        }
      ]
    : []),
  {
    element: <PersistLogin />,
    children: [
      {
        element: <AxiosInterceptor />,
        children: [
          {
            element: (
              <RequireAuth
                allowedAccounts={[TypeAccount.admin, TypeAccount.user, TypeAccount.advisor]}
              />
            ),
            children: [
              {
                path: SEGMENTS.admin.root,
                element: (
                  <WhatsNewProvider>
                    <LayoutAdmin />
                  </WhatsNewProvider>
                ),
                errorElement: <RouteErrorBoundary />,
                children: [
                  {
                    index: true,
                    element: <Navigate to={SEGMENTS.admin.dashboard} />
                  },
                  {
                    path: `${SEGMENTS.admin.dashboard}/*`,
                    element: <Dashboard />
                  },
                  {
                    element: <NavigateAuthBusinessAdmin isRouter />,
                    children: [
                      {
                        path: `${SEGMENTS.admin.business}/*`,
                        element: (
                          <AdminSuspenseWrapper>
                            <RouterBusiness />
                          </AdminSuspenseWrapper>
                        ),
                        errorElement: <RouteErrorBoundary />
                      }
                    ]
                  },
                  {
                    element: (
                      <ProtectedRoute
                        permission={[
                          Permission.CreateNegotiation,
                          Permission.ReadAllNegotiations,
                          Permission.UpdateNegotiation
                        ]}
                        redirectTo={`/${SEGMENTS.admin.root}/${SEGMENTS.admin.unauthorized}`}
                      />
                    ),
                    children: [
                      {
                        path: `${SEGMENTS.admin.negotiations}/*`,
                        element: (
                          <AdminSuspenseWrapper>
                            <RouterNegotiations />
                          </AdminSuspenseWrapper>
                        ),
                        errorElement: <RouteErrorBoundary />
                      }
                    ]
                  },
                  {
                    element: (
                      <ProtectedRoute
                        permission={Permission.ReadAllDeliveries}
                        redirectTo={`/${SEGMENTS.admin.root}/${SEGMENTS.admin.unauthorized}`}
                      />
                    ),
                    children: [
                      {
                        path: `${SEGMENTS.admin.deliveries}/*`,
                        element: (
                          <AdminSuspenseWrapper>
                            <RouterDeliveries />
                          </AdminSuspenseWrapper>
                        ),
                        errorElement: <RouteErrorBoundary />
                      }
                    ]
                  },
                  {
                    element: (
                      <ProtectedRoute
                        permission={Permission.ReadAvailability}
                        redirectTo={`/${SEGMENTS.admin.root}/${SEGMENTS.admin.unauthorized}`}
                      />
                    ),
                    children: [
                      {
                        path: `${SEGMENTS.admin.inventory}/*`,
                        element: (
                          <AdminSuspenseWrapper>
                            <RouterInventory />
                          </AdminSuspenseWrapper>
                        ),
                        errorElement: <RouteErrorBoundary />
                      }
                    ]
                  },
                  {
                    element: (
                      <ProtectedRoute
                        permission={[
                          Permission.CreateClient,
                          Permission.ReadAllClients,
                          Permission.UpdateClient
                        ]}
                        redirectTo={`/${SEGMENTS.admin.root}/${SEGMENTS.admin.unauthorized}`}
                      />
                    ),
                    children: [
                      {
                        path: `${SEGMENTS.admin.clients}/*`,
                        element: (
                          <AdminSuspenseWrapper>
                            <RouterClients />
                          </AdminSuspenseWrapper>
                        ),
                        errorElement: <RouteErrorBoundary />
                      }
                    ]
                  },
                  {
                    path: SEGMENTS.admin.creditos,
                    children: creditsRoutes,
                    errorElement: <RouteErrorBoundary />
                  },
                  {
                    path: `${SEGMENTS.account.root}/${SEGMENTS.account.apiTokens}`,
                    children: apiTokenRoutes,
                    errorElement: <RouteErrorBoundary />
                  },
                  {
                    path: `${SEGMENTS.account.root}/*`,
                    element: (
                      <AdminSuspenseWrapper>
                        <RouterAccounts />
                      </AdminSuspenseWrapper>
                    ),
                    errorElement: <RouteErrorBoundary />
                  },
                  // `/flows` es el motor de flujos DEL PORTAL —define qué secciones llena el
                  // cliente—, así que va bajo el mismo flag y no bajo uno propio. No confundir
                  // con `/workflows`, que es de negociaciones y sale siempre.
                  ...(FEATURES.clientPortal
                    ? [
                        {
                          path: SEGMENTS.admin.flows,
                          children: flowsRoutes,
                          errorElement: <RouteErrorBoundary />
                        },
                        {
                          path: SEGMENTS.admin.revalidation,
                          children: revalidationRoutes,
                          errorElement: <RouteErrorBoundary />
                        }
                      ]
                    : []),
                  {
                    path: SEGMENTS.admin.changelog,
                    element: (
                      <AdminSuspenseWrapper>
                        <ChangelogPage />
                      </AdminSuspenseWrapper>
                    )
                  },
                  {
                    path: SEGMENTS.admin.unauthorized,
                    element: <Unauthorized />
                  },
                  {
                    path: '*',
                    element: <NotFound />
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    path: '*',
    element: <NotFound />
  }
] satisfies RouteObject[]

const DocumentHeadLayout = () => (
  <>
    <DocumentHead />
    <Outlet />
  </>
)

export const router = createBrowserRouter([
  {
    element: <DocumentHeadLayout />,
    children: routes
  }
])

export default router
